"use client";

import { c as _c } from "react/compiler-runtime";
import { api } from "@agroproj/backend/convex/_generated/api";
import type { Id } from "@agroproj/backend/convex/_generated/dataModel";
import type { ProjectItem } from "@agroproj/backend/convex/types";
import { useQuery } from "convex/react";
import { useMemo } from "react";
import { useDebounce } from "use-debounce";
import { projectWorkflowStatusEnum } from "@/data/entity-types";
import { useStableQuery } from "@/hooks/use-stable-query";
type ProjectStatus = "open" | "completed" | "canceled";
type ProjectWorkflowStatus = keyof typeof projectWorkflowStatusEnum;
function isProjectWorkflowStatus(value: string): value is ProjectWorkflowStatus {
  return Object.hasOwn(projectWorkflowStatusEnum, value);
}
type UseProjectsDataProps = {
  search?: string;
  status?: string[];
  /** Workflow stages from the `workflowStatus` URL param; unknown values are ignored. */
  workflowStatus?: string[];
  activity?: string[];
  harvest?: string[];
  classification?: string[];
  program?: string[];
  updatedFrom?: string;
  updatedTo?: string;
  technicalProjectType?: "operating_cost" | "investment";
};
function parseDateBoundary(value: string, endOfDay: boolean) {
  const suffix = endOfDay ? "T23:59:59.999" : "T00:00:00";
  const parsed = new Date(`${value}${suffix}`);
  if (Number.isNaN(parsed.getTime())) {
    return undefined;
  }
  return parsed.getTime();
}
export function useProjectsData(t0) {
  const $ = _c(70);
  const {
    search: t1,
    status: t2,
    workflowStatus: t3,
    activity: t4,
    harvest: t5,
    classification: t6,
    program: t7,
    updatedFrom: t8,
    updatedTo: t9,
    technicalProjectType
  } = t0;
  const search = t1 === undefined ? "" : t1;
  let t10;
  if ($[0] !== t2) {
    t10 = t2 === undefined ? [] : t2;
    $[0] = t2;
    $[1] = t10;
  } else {
    t10 = $[1];
  }
  const status = t10;
  let t11;
  if ($[2] !== t3) {
    t11 = t3 === undefined ? [] : t3;
    $[2] = t3;
    $[3] = t11;
  } else {
    t11 = $[3];
  }
  const workflowStatus = t11;
  let t12;
  if ($[4] !== t4) {
    t12 = t4 === undefined ? [] : t4;
    $[4] = t4;
    $[5] = t12;
  } else {
    t12 = $[5];
  }
  const activity = t12;
  let t13;
  if ($[6] !== t5) {
    t13 = t5 === undefined ? [] : t5;
    $[6] = t5;
    $[7] = t13;
  } else {
    t13 = $[7];
  }
  const harvest = t13;
  let t14;
  if ($[8] !== t6) {
    t14 = t6 === undefined ? [] : t6;
    $[8] = t6;
    $[9] = t14;
  } else {
    t14 = $[9];
  }
  const classification = t14;
  let t15;
  if ($[10] !== t7) {
    t15 = t7 === undefined ? [] : t7;
    $[10] = t7;
    $[11] = t15;
  } else {
    t15 = $[11];
  }
  const program = t15;
  const updatedFrom = t8 === undefined ? "" : t8;
  const updatedTo = t9 === undefined ? "" : t9;
  const [debouncedSearch] = useDebounce(search, 500);
  let t16;
  bb0: {
    if (status.length <= 1) {
      t16 = undefined;
      break bb0;
    }
    let t17;
    if ($[12] !== status) {
      t17 = [...new Set(status)].sort();
      $[12] = status;
      $[13] = t17;
    } else {
      t17 = $[13];
    }
    t16 = t17 as ProjectStatus[];
  }
  const statusesFilter = t16;
  let t17;
  bb1: {
    if (status.length !== 1) {
      t17 = undefined;
      break bb1;
    }
    t17 = status[0] as ProjectStatus;
  }
  const statusFilter = t17;
  let t18;
  if ($[14] !== workflowStatus) {
    t18 = [...new Set(workflowStatus.filter(isProjectWorkflowStatus))].sort();
    $[14] = workflowStatus;
    $[15] = t18;
  } else {
    t18 = $[15];
  }
  const valid = t18;
  const workflowStatusesFilter = valid.length > 0 ? valid : undefined;
  const rawProducerClassifications = useQuery(api.base.queries.getProjectProducerClassifications);
  const isLoadingClassifications = rawProducerClassifications === undefined;
  let t19;
  bb2: {
    if (classification.length === 0 || !rawProducerClassifications) {
      t19 = undefined;
      break bb2;
    }
    let t20;
    if ($[16] !== classification || $[17] !== rawProducerClassifications) {
      let t21;
      if ($[19] !== rawProducerClassifications) {
        t21 = title => rawProducerClassifications.find(item => item.title === title)?._id;
        $[19] = rawProducerClassifications;
        $[20] = t21;
      } else {
        t21 = $[20];
      }
      const ids = classification.map(t21).filter(_temp);
      t20 = ids.length > 0 ? [...new Set(ids)].sort() : undefined;
      $[16] = classification;
      $[17] = rawProducerClassifications;
      $[18] = t20;
    } else {
      t20 = $[18];
    }
    t19 = t20;
  }
  const classificationIds = t19;
  let t20;
  if ($[21] !== technicalProjectType || $[22] !== updatedFrom) {
    t20 = technicalProjectType === "investment" && updatedFrom ? parseDateBoundary(updatedFrom, false) : undefined;
    $[21] = technicalProjectType;
    $[22] = updatedFrom;
    $[23] = t20;
  } else {
    t20 = $[23];
  }
  const updatedFromTimestamp = t20;
  let t21;
  if ($[24] !== technicalProjectType || $[25] !== updatedTo) {
    t21 = technicalProjectType === "investment" && updatedTo ? parseDateBoundary(updatedTo, true) : undefined;
    $[24] = technicalProjectType;
    $[25] = updatedTo;
    $[26] = t21;
  } else {
    t21 = $[26];
  }
  const updatedToTimestamp = t21;
  let t22;
  if ($[27] !== debouncedSearch) {
    t22 = debouncedSearch.trim().length >= 2 ? debouncedSearch.trim().toLowerCase() : undefined;
    $[27] = debouncedSearch;
    $[28] = t22;
  } else {
    t22 = $[28];
  }
  let t23;
  if ($[29] !== activity) {
    t23 = activity.length > 0 ? [...new Set(activity)].sort() as Array<Id<"base_activities">> : undefined;
    $[29] = activity;
    $[30] = t23;
  } else {
    t23 = $[30];
  }
  let t24;
  if ($[31] !== harvest) {
    t24 = harvest.length > 0 ? [...new Set(harvest)].sort() : undefined;
    $[31] = harvest;
    $[32] = t24;
  } else {
    t24 = $[32];
  }
  let t25;
  if ($[33] !== program) {
    t25 = program.length > 0 ? [...new Set(program)].sort() : undefined;
    $[33] = program;
    $[34] = t25;
  } else {
    t25 = $[34];
  }
  let t26;
  if ($[35] !== classificationIds || $[36] !== statusFilter || $[37] !== statusesFilter || $[38] !== t22 || $[39] !== t23 || $[40] !== t24 || $[41] !== t25 || $[42] !== technicalProjectType || $[43] !== updatedFromTimestamp || $[44] !== updatedToTimestamp || $[45] !== workflowStatusesFilter) {
    t26 = {
      status: statusFilter,
      statuses: statusesFilter,
      workflowStatuses: workflowStatusesFilter,
      search: t22,
      technicalProjectType,
      activityIds: t23,
      harvests: t24,
      classificationIds,
      programCodes: t25,
      updatedFrom: updatedFromTimestamp,
      updatedTo: updatedToTimestamp
    };
    $[35] = classificationIds;
    $[36] = statusFilter;
    $[37] = statusesFilter;
    $[38] = t22;
    $[39] = t23;
    $[40] = t24;
    $[41] = t25;
    $[42] = technicalProjectType;
    $[43] = updatedFromTimestamp;
    $[44] = updatedToTimestamp;
    $[45] = workflowStatusesFilter;
    $[46] = t26;
  } else {
    t26 = $[46];
  }
  const queryArgs = t26;
  let t27;
  bb3: {
    if (!rawProducerClassifications) {
      let t28;
      if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
        t28 = [];
        $[47] = t28;
      } else {
        t28 = $[47];
      }
      t27 = t28;
      break bb3;
    }
    let t28;
    if ($[48] !== rawProducerClassifications) {
      t28 = rawProducerClassifications.map(_temp2);
      $[48] = rawProducerClassifications;
      $[49] = t28;
    } else {
      t28 = $[49];
    }
    t27 = t28;
  }
  const producerClassifications = t27;
  let t28;
  if ($[50] !== technicalProjectType) {
    t28 = {
      retentionKey: technicalProjectType
    };
    $[50] = technicalProjectType;
    $[51] = t28;
  } else {
    t28 = $[51];
  }
  const {
    data: projectsData,
    isStale
  } = useStableQuery(api.projects.queries.getAllProjects, t28, queryArgs);
  let t29;
  bb4: {
    if (!projectsData) {
      let t30;
      if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
        t30 = [];
        $[52] = t30;
      } else {
        t30 = $[52];
      }
      t29 = t30;
      break bb4;
    }
    let t30;
    if ($[53] !== projectsData || $[54] !== rawProducerClassifications) {
      let t31;
      if ($[56] !== rawProducerClassifications) {
        t31 = project => {
          const createdAt = project.createdAt ?? project._creationTime;
          const updatedAt = project.updatedAt ?? project._creationTime;
          const classificationFromList = rawProducerClassifications?.find(item_0 => item_0._id === project.producerClassificationId);
          const resolvedClassification = project.producerClassification ? {
            id: project.producerClassification._id,
            title: project.producerClassification.title
          } : classificationFromList ? {
            id: classificationFromList._id,
            title: classificationFromList.title
          } : undefined;
          return {
            ...project,
            id: project._id,
            projectCode: project.projectCode || "",
            status: project.status,
            harvest: project.harvest || "",
            createdAt: new Date(createdAt),
            updatedAt: new Date(updatedAt),
            producer: project.producer ? {
              _id: project.producer._id,
              name: project.producer.name ?? undefined,
              companyName: project.producer.companyName ?? undefined,
              documentType: project.producer.documentType,
              documentValue: project.producer.documentValue,
              primaryAddress: project.producer.primaryAddress ? {
                street: project.producer.primaryAddress.street,
                number: project.producer.primaryAddress.number,
                neighborhood: project.producer.primaryAddress.neighborhood,
                city: project.producer.primaryAddress.city,
                state: project.producer.primaryAddress.state,
                postalCode: project.producer.primaryAddress.postalCode
              } : undefined
            } : undefined,
            projectType: project.projectTypeInfo ? {
              _id: project.projectTypeInfo._id,
              title: project.projectTypeInfo.title ?? ""
            } : undefined,
            projectTypeCode: project.projectType,
            activity: project.activity ? {
              _id: project.activity._id,
              id: project.activity._id,
              title: project.activity.title ?? ""
            } : undefined,
            producerClassification: resolvedClassification,
            financingFramework: project.financingFramework ? {
              id: project.financingFramework._id,
              code: project.financingFramework.code,
              title: project.financingFramework.title
            } : undefined,
            bank: project.bank ?? null,
            technicalType: project.technicalTypeInfo ?? null,
            investmentProgram: project.investmentProgram ?? null,
            benefitedAreas: [],
            supplies: [],
            services: []
          };
        };
        $[56] = rawProducerClassifications;
        $[57] = t31;
      } else {
        t31 = $[57];
      }
      t30 = projectsData.map(t31);
      $[53] = projectsData;
      $[54] = rawProducerClassifications;
      $[55] = t30;
    } else {
      t30 = $[55];
    }
    t29 = t30;
  }
  const projects = t29;
  const isLoadingProjects = projectsData === undefined;
  const isLoading = isLoadingProjects;
  let t30;
  if ($[58] !== harvest || $[59] !== projects) {
    const harvestSet = new Set(harvest);
    for (const project_0 of projects) {
      if (project_0.harvest && typeof project_0.harvest === "string" && project_0.harvest.length > 0) {
        harvestSet.add(project_0.harvest);
      }
    }
    t30 = Array.from(harvestSet).sort();
    $[58] = harvest;
    $[59] = projects;
    $[60] = t30;
  } else {
    t30 = $[60];
  }
  const uniqueHarvests = t30;
  const t31 = search !== debouncedSearch;
  let t32;
  if ($[61] !== isLoading || $[62] !== isLoadingClassifications || $[63] !== isLoadingProjects || $[64] !== isStale || $[65] !== producerClassifications || $[66] !== projects || $[67] !== t31 || $[68] !== uniqueHarvests) {
    t32 = {
      projects,
      uniqueHarvests,
      producerClassifications,
      isLoading,
      isLoadingProjects,
      isLoadingClassifications,
      isStale,
      isSearching: t31
    };
    $[61] = isLoading;
    $[62] = isLoadingClassifications;
    $[63] = isLoadingProjects;
    $[64] = isStale;
    $[65] = producerClassifications;
    $[66] = projects;
    $[67] = t31;
    $[68] = uniqueHarvests;
    $[69] = t32;
  } else {
    t32 = $[69];
  }
  return t32;
}
function _temp2(classification_0) {
  return {
    id: classification_0._id,
    title: classification_0.title
  };
}
function _temp(id) {
  return Boolean(id);
}